import { VALIDATION_MARKER_TYPE } from '../../controller/functions/validation-markers/validation-marker.helper';
import { VENT_CUTOUT_TYPE } from '../engine/managers/cutout-manager/helpers/vent-proxy.helpers';

/** Publishes onto the consuming controller's pub/sub, already bound to it. */
export type SceneObjectPublish = (noun: string, action: string, payload: unknown, payloadType?: string) => void;

export type SceneObjectClickParams = {
	/** `click`, `double-click` or `right-click` */
	action: string;
	objectId: string;
	userData: Record<string, unknown>;
	publish: SceneObjectPublish;
};

export type SceneObjectClickHandler = (params: SceneObjectClickParams) => void;

export type SceneObjectTypeRegistration = {
	/** The `userData.type` this applies to */
	type: string;
	/** Wins a hit over anything geometrically nearer — camera-facing badges and labels draw on top */
	overlay?: boolean;
	/** The raycaster looks straight through it — debug aids, drag indicators, hidden cutout clones */
	nonInteractive?: boolean;
	/** Lights up under the pointer while hover tracking is on */
	hoverGlow?: boolean;
	/** Replaces the default `object`/`selections` publish for clicks on this type */
	onClick?: SceneObjectClickHandler;
};

const overlayTypes = new Set<string>();
const nonInteractiveTypes = new Set<string>();
const hoverGlowTypes = new Set<string>();
const clickHandlers = new Map<string, SceneObjectClickHandler>();

/**
 * Tell the scene how to treat a `userData.type` it will meet while raycasting. Consumers call this
 * to add their own overlays — a floating bubble, say — without editing the raycaster.
 * @param registration what the type is and how clicks on it should publish
 */
export const registerSceneObjectType = (registration: SceneObjectTypeRegistration): void => {
	const { type, overlay = false, nonInteractive = false, hoverGlow = false, onClick } = registration;
	if (overlay) overlayTypes.add(type);
	if (nonInteractive) nonInteractiveTypes.add(type);
	if (hoverGlow) hoverGlowTypes.add(type);
	if (onClick) clickHandlers.set(type, onClick);
};

/** Drop a registration again — a dev view that unmounts should leave no handler behind. */
export const unregisterSceneObjectType = (type: string): void => {
	overlayTypes.delete(type);
	nonInteractiveTypes.delete(type);
	hoverGlowTypes.delete(type);
	clickHandlers.delete(type);
};

export const isOverlayObjectType = (type: unknown): boolean => typeof type === 'string' && overlayTypes.has(type);

export const isNonInteractiveObjectType = (type: unknown): boolean => typeof type === 'string' && nonInteractiveTypes.has(type);

export const isHoverGlowObjectType = (type: unknown): boolean => typeof type === 'string' && hoverGlowTypes.has(type);

export const getSceneObjectClickHandler = (type: unknown): SceneObjectClickHandler | null =>
	(typeof type === 'string' ? clickHandlers.get(type) ?? null : null);

const BUILT_IN: Array<SceneObjectTypeRegistration> = [
	// Pre-subtraction clones the cutout manager keeps hidden; three raycasts invisible meshes, so
	// they would otherwise answer for the object they were cloned from.
	{ type: 'appliance-cutout', nonInteractive: true },
	{ type: 'appliance-dissolving', nonInteractive: true },
	{ type: 'countertop-no-cutout', nonInteractive: true },
	{ type: 'debug', nonInteractive: true },
	{ type: 'drag-indicator', nonInteractive: true },
	{ type: 'panel-no-cutout', nonInteractive: true },
	{ type: 'rotation-gizmo', nonInteractive: true },
	// Draw on top (renderOrder/depthTest) but sort by distance like any mesh, so without priority
	// the panel behind one swallows the click.
	// The parts of the kitchen a viewer can hover. The glow only happens while a consumer has hover
	// tracking on, so declaring them here costs nothing by default.
	{ type: 'appliance', hoverGlow: true },
	{ type: 'corner', hoverGlow: true },
	{ type: 'countertop', hoverGlow: true },
	{ type: 'panel', hoverGlow: true },
	// The exception among the cutout proxies: a vent IS the hole, so its proxy is the vent as far as
	// the pointer and the safety legend are concerned.
	{ type: VENT_CUTOUT_TYPE, hoverGlow: true },
	{ type: 'selection-label', overlay: true },
	{ type: VALIDATION_MARKER_TYPE, overlay: true }
];

BUILT_IN.forEach((registration) => registerSceneObjectType(registration));
